import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum } from '@/app/utils/constants';
import {
  ContractsIcon,
  Delete02Icon,
  MoreHorizontalIcon,
  CircleLock01Icon,
  CircleUnlock01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/app/ui/components/menu';
import { useSetAtom } from 'jotai';
import {
  isDeleteSheetModalOpenAtom,
  sheetIdToDeleteAtom,
} from '@/app/components/features/workPackages/store';
import { WorkPackagesLayoutContent } from '@/app/components/features/workPackages/WorkPackagesLayoutContent';
import { getContractDetailsQuery } from '@/app/hooks/queries/useContracts';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import {
  useGetWpSheetLayout,
  useLockWpSheet,
  useUnlockWpSheet,
} from '@/app/hooks/queries/useNewWorkPackages';
import { useContext } from 'react';
import { AppContext } from '@/app/appContext';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Outlet } from '@tanstack/react-router';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/contracts/$contractId/_layout/(main)/work-packages/$sheetId/_layout',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.contractHome');
  const tFeature = useTranslations('features.workPackages');
  const tWp = useTranslations('components.workPackages.newUi');
  const { contractId, projectId, sheetId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getContractDetailsQuery(contractId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));

  const { data: sheetLayout } = useGetWpSheetLayout({
    sheetId,
    type: 'Contract',
  });
  const { userMeta } = useContext(AppContext);
  const setIsDeleteSheetModalOpen = useSetAtom(isDeleteSheetModalOpenAtom);
  const setSheetIdToDelete = useSetAtom(sheetIdToDeleteAtom);

  const { mutateAsync: lockSheet, isPending: isLockingSheet } = useLockWpSheet(
    'Contract',
    contractId,
  );

  const { mutateAsync: unlockSheet, isPending: isUnlockingSheet } = useUnlockWpSheet(
    'Contract',
    contractId,
  );

  const isSheetLocked = sheetLayout?.metadataInfo?.isLocked || false;

  const openDeleteSheetModal = (sheetId: string) => {
    setSheetIdToDelete(sheetId);
    setIsDeleteSheetModalOpen(true);
  };

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/contracts/$contractId"
          params={{ projectId, contractId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.CONTRACT} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/contracts/$contractId"
                params={{ projectId, contractId }}
              >
                <HugeiconsIcon icon={ContractsIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Menu>
          <MenuTrigger
            render={
              <HeaderButton className="md:flex">
                <HugeiconsIcon icon={MoreHorizontalIcon} className="size-5.5 md:size-5" />
              </HeaderButton>
            }
          />
          <MenuPopup align="start">
            {sheetId && isSheetLocked && (
              <MenuItem
                onClick={async () => {
                  if (!userMeta.id || !sheetId) return;
                  await unlockSheet({ sheetId, userId: userMeta.id });
                }}
                disabled={isUnlockingSheet || sheetLayout?.metadataInfo?.createdBy !== userMeta.id}
              >
                <HugeiconsIcon icon={CircleUnlock01Icon} className="size-4" />
                {tWp('buttons.unlock')}
              </MenuItem>
            )}
            {sheetId && !isSheetLocked && (
              <MenuItem
                onClick={async () => {
                  if (!userMeta.id || !sheetId) return;
                  await lockSheet({ sheetId, userId: userMeta.id });
                }}
                disabled={isLockingSheet || sheetLayout?.metadataInfo?.createdBy !== userMeta.id}
              >
                <HugeiconsIcon icon={CircleLock01Icon} className="size-4" />
                {tWp('buttons.lock')}
              </MenuItem>
            )}
            <MenuItem
              variant="destructive"
              onClick={() => sheetId && openDeleteSheetModal(sheetId)}
            >
              <HugeiconsIcon icon={Delete02Icon} className="size-4" />
              {tWp('buttons.delete')}
            </MenuItem>
          </MenuPopup>
        </Menu>
        <HeaderSidechatButton />
      </Header>
      <WorkPackagesLayoutContent
        evaluationId={contractId}
        type="Contract"
        evalLanguage={evaluation.language}
      >
        <Outlet />
      </WorkPackagesLayoutContent>
    </>
  );
}
